import React from "react";
import { createRoot } from "react-dom/client";

/**
 * Универсальный модуль для монтирования и размонтирования React компонентов в Turbo приложениях
 * 
 * Использование:
 * import { createReactMounter } from "./lib/react_mounter.js";
 * 
 * const COMPONENTS = {
 *   MyComponent1,
 *   MyComponent2,
 * };
 * 
 * const mounter = createReactMounter(COMPONENTS);
 * mounter.initializeApp();
 */

class ReactMounter {
  constructor(components = {}) {
    this.components = components;
    this.reactRoots = new Map();
    this.eventListenersAdded = false;
    
    // Привязываем методы к контексту для корректной работы в event listeners
    this.mountComponents = this.mountComponents.bind(this);
    this.unmountAllComponents = this.unmountAllComponents.bind(this);
  }

  /**
   * Инициализирует приложение - монтирует существующие компоненты и настраивает event listeners
   */
  initializeApp() {
    this.mountComponents();
    this.setupEventListeners();
  }

  /**
   * Монтирует все React компоненты на странице
   */
  mountComponents() {
    document.querySelectorAll("[data-react-class]").forEach((node) => {
      if (node.dataset.reactMounted) return;
      
      this.mountSingleComponent(node);
    });
  }

  /**
   * Монтирует отдельный React компонент
   * @param {HTMLElement} node - DOM элемент для монтирования
   */
  mountSingleComponent(node) {
    node.dataset.reactMounted = true;

    const componentName = node.getAttribute("data-react-class");
    const props = node.getAttribute("data-react-props") || "{}";
    const Component = this.components[componentName];
    
    if (!Component) {
      console.warn(`React component "${componentName}" not found in registered components`);
      return;
    }

    // Если уже был смонтирован — размонтируем
    if (this.reactRoots.has(node)) {
      this.reactRoots.get(node).unmount();
      this.reactRoots.delete(node);
    }

    try {
      const parsedProps = JSON.parse(props);
      const root = createRoot(node);
      root.render(<Component {...parsedProps} />);
      this.reactRoots.set(node, root);
    } catch (error) {
      console.error(`Error mounting React component "${componentName}":`, error);
    }
  }

  /**
   * Размонтирует все React компоненты
   */
  unmountAllComponents() {
    console.log("Unmounting all React components");
    this.reactRoots.forEach((root, node) => {
      try {
        root.unmount();
        node.dataset.reactMounted = false;
      } catch (error) {
        console.error("Error unmounting React component:", error);
      }
    });
    this.reactRoots.clear();
  }

  /**
   * Настраивает event listeners для Turbo
   */
  setupEventListeners() {
    if (this.eventListenersAdded) return;
    
    this.eventListenersAdded = true;
    
    // Монтируем компоненты после рендера страницы
    document.addEventListener("turbo:render", this.mountComponents);
    
    // Размонтируем компоненты перед рендером новой страницы
    document.addEventListener("turbo:before-render", this.unmountAllComponents);
  }

  /**
   * Удаляет event listeners (полезно для тестов или при необходимости очистки)
   */
  cleanup() {
    this.unmountAllComponents();
    document.removeEventListener("turbo:render", this.mountComponents);
    document.removeEventListener("turbo:before-render", this.unmountAllComponents);
    this.eventListenersAdded = false;
  }

  /**
   * Добавляет новые компоненты в реестр
   * @param {Object} newComponents - объект с компонентами
   */
  registerComponents(newComponents) {
    this.components = { ...this.components, ...newComponents };
  }
}

/**
 * Фабричная функция для создания нового экземпляра ReactMounter
 * @param {Object} components - объект с React компонентами
 * @returns {ReactMounter}
 */
export function createReactMounter(components = {}) {
  return new ReactMounter(components);
}

/**
 * Singleton экземпляр для глобального использования
 */
export const globalReactMounter = new ReactMounter();

export default ReactMounter;